import { capitalize } from "lodash";
import { KPI_IMAGE_PARAMS } from "../constants/kpi";
import { formatNumber } from "./formatter";
import { isBidAccuracyKpi } from "./scoreAggregate";

export const HCP_JOB_TAGS = ["solo tech", "lead (with assistant)", "two leads", "training"];

export function getHcpTagColor(tag) {
  if (tag === "solo tech") return "#00C853";
  if (tag === "lead (with assistant)") return "#2979ff";
  if (tag === "training") return "#F4511E";
  return "#a929ff";
}

export function getTimelineJobId(timeline) {
  if (timeline.refModel === "Job") return timeline.refId;
  if (timeline.claimModel === "Job") return timeline.claimId;
  if (timeline.metadata?.refModel === "Job") return timeline.metadata.refId;
  return null;
}

export function getTimelineRefModel(timeline) {
  if (timeline.refModel) return timeline.refModel;
  if (timeline.metadata?.refModel) return timeline.metadata.refModel;
  return null;
}

export function getTimelineRefId(timeline) {
  const refModel = getTimelineRefModel(timeline);
  if (!refModel) return null;
  if (refModel === "Job") return getTimelineJobId(timeline);
  if (timeline.refModel === refModel) return timeline.refId;
  if (timeline.metadata?.refModel === refModel) return timeline.metadata.refId;
  return timeline.refId || timeline.metadata?.refId || null;
}

export function isIntegratedTimelineEntry(timeline) {
  return timeline.scoreType === "INTEGRATED";
}

export function isIntegratedJobEntry(timeline) {
  return isIntegratedTimelineEntry(timeline) && Boolean(getTimelineJobId(timeline));
}

export function formatTimelineMetricTitle(kpiName, amount, kind) {
  const name = kpiName || "Score";
  return `${name} - ${formatNumber(Math.abs(Number(amount) || 0), kind)}`;
}

export function formatJobLeadName(metadata) {
  if (!metadata) return null;
  const customer = metadata.customerName?.trim() || metadata.name?.trim();
  const description = metadata.description?.trim() || metadata.content?.trim();
  if (customer && description) return `${customer} — ${description}`;
  return customer || description || null;
}

export function formatIntegratedRefSubtitle(metadata, refPreview) {
  const source = refPreview || metadata;
  if (!source) return null;

  return formatJobLeadName({
    customerName: source.name || source.customerName || source.metadata?.customerName,
    description:
      source.content ||
      source.description ||
      source.metadata?.description ||
      source.metadata?.content,
  });
}

export function formatReviewRatingBadge(rating) {
  if (rating == null || Number.isNaN(Number(rating))) return null;
  return `${Number(rating)}★`;
}

// Canonical form of a metadata key: production Memberships metrics store the
// plan under `plan_name ` (trailing space, In-House), `plan_name_` (trailing
// underscore, Sales) and `plan_name` (HCP sweep) — all one field.
const canonicalMetaKey = (key) => String(key).trim().replace(/[\s_]+$/, "");

export function getMembershipPlanName(metadata) {
  if (!metadata) return null;
  const key = Object.keys(metadata).find((k) => canonicalMetaKey(k) === "plan_name");
  const value = key != null ? metadata[key] : null;
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function isMembershipTimelineEntry(timeline) {
  return Boolean(getMembershipPlanName(timeline?.metadata));
}

export function resolveJobTag(timeline, job) {
  const metadataTag = timeline.metadata?.tag?.toLowerCase?.();
  if (metadataTag && HCP_JOB_TAGS.includes(metadataTag)) {
    return timeline.metadata.tag;
  }

  const jobTags = Array.isArray(job?.tags) ? job.tags : [];
  const matched = jobTags.find((tag) =>
    HCP_JOB_TAGS.includes(String(tag).toLowerCase()),
  );
  return matched || null;
}

/** Role tag from a Job document for feed cards (null when not tagged). */
export function resolveJobRoleTag(job) {
  const jobTags = Array.isArray(job?.tags) ? job.tags : [];
  return (
    jobTags.find((tag) => HCP_JOB_TAGS.includes(String(tag).toLowerCase())) ||
    null
  );
}

export function findKpiInCatalog(kpiId, catalogs = []) {
  if (kpiId == null) return null;
  const targetId = String(kpiId);

  for (let i = 0; i < catalogs.length; i += 1) {
    const catalog = catalogs[i];
    if (!catalog) continue;

    if (Array.isArray(catalog)) {
      const match = catalog.find((kpi) => String(kpi._id) === targetId);
      if (match) return match;
      continue;
    }

    if (typeof catalog === "object") {
      const lists = Object.values(catalog);
      for (let j = 0; j < lists.length; j += 1) {
        const list = lists[j];
        if (!Array.isArray(list)) continue;
        const match = list.find((kpi) => String(kpi._id) === targetId);
        if (match) return match;
      }
    }
  }

  return null;
}

export function resolveKpiDisplayName(kpi) {
  if (!kpi) return "";
  return kpi.title || kpi.name || "";
}

const MANUAL_SCORE_TYPES = new Set([
  "ADMIN_ENTERED",
  "PLAYER_ENTERED",
  "ADMIN_ADJUSTED",
]);

export function isManualTimelineEntry(scoreType) {
  return MANUAL_SCORE_TYPES.has(scoreType);
}

// Friendly labels for known integrated metadata (Estimate Won Value, etc.).
const DETAIL_LABELS = {
  estimateTotal: { label: "Estimate total", type: "currency" },
  estimateLabor: { label: "Labor", type: "currency" },
  estimateDiscountPct: { label: "Discount", type: "percent" },
  estimateDiscountFixed: { label: "Fixed discount", type: "currency" },
  customerName: { label: "Customer", type: "text" },
  customer_name: { label: "Customer", type: "text" },
  plan_name: { label: "Plan", type: "text" },
  invoiceNumber: { label: "Invoice #", type: "text" },
  jobInvoiceNumber: { label: "Invoice #", type: "text" },
  jobLabor: { label: "Line Items Total", type: "currency" },
  jobDiscountPct: { label: "Discount", type: "percent" },
  jobDiscountFixed: { label: "Fixed discount", type: "currency" },
  jobCompletedAt: { label: "Job completed", type: "datetime" },
  jobScheduledAt: { label: "Scheduled", type: "datetime" },
  scheduledStart: { label: "Scheduled", type: "datetime" },
  scheduledAt: { label: "Scheduled", type: "datetime" },
  scheduledDate: { label: "Scheduled", type: "datetime" },
  soldDate: { label: "Sold date", type: "datetime" },
  createdDate: { label: "Created", type: "datetime" },
  share: { label: "Rate", type: "rate" },
};

// Internal/technical metadata keys that should never be shown to users.
const HIDDEN_DETAIL_KEYS = new Set([
  "refModel",
  "refId",
  "extRefDate",
  "subtotalFromLineItems",
  "imageRotation",
  "orientationSaved",
  "deleted",
  "deletedAt",
  "tag",
  "technicianName",
  "claimAction",
  "actorId",
  "via",
  // Legacy Estimate Won Value keys (formula is now labor − discounts).
  "estimateTax",
  "estimateFuel",
  "estimateSupplies",
  // Membership sync internals (plan_name / customer_name / invoiceNumber are
  // the user-facing ones).
  "rawPlanName",
  "hcpJobId",
  "hcpInvoiceId",
  "originEstimateRef",
  "invoiceStatus",
  "shareBackfilledFrom",
]);

// Turns a metadata object into labeled, human-readable rows for "View Details".
// Known keys get a label + formatting; unknown keys fall back to their raw value
// (preserves existing manual-note behavior); technical keys are hidden.
// `share` is labeled "Rate" below (not hidden) so Commission View Details shows 1%.
export function buildDetailRows(metadata) {
  if (!metadata) return [];

  const rows = [];
  const seenLabeled = new Set();
  Object.entries(metadata).forEach(([key, value]) => {
    if (value == null || value === "" || typeof value === "object") return;
    // Production membership keys carry a trailing space (`plan_name `) or
    // underscore (`plan_name_`) — match labels/hides on the canonical name.
    const keyName = canonicalMetaKey(key);
    if (HIDDEN_DETAIL_KEYS.has(keyName)) return;
    if (
      (keyName === "estimateDiscountPct" || keyName === "estimateDiscountFixed") &&
      Number(value) === 0
    ) {
      return;
    }

    const def = DETAIL_LABELS[keyName];
    const label = def?.label ?? null;
    let display = String(value);

    if (def?.type === "currency") {
      display = formatNumber(Number(value), "$");
    } else if (def?.type === "percent") {
      display = `${Number(value)}%`;
    } else if (def?.type === "rate") {
      // parameters.share / metadata.share is a fraction (0.01 → 1%).
      const n = Number(value);
      display = Number.isFinite(n) ? `${Number((n * 100).toFixed(2))}%` : String(value);
    } else if (def?.type === "datetime") {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        display = d.toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
      }
    }

    // Membership entries store the customer under both `customer_name` and
    // `customerName` — one "Customer" row is enough.
    if (label) {
      const sig = `${label}|${display}`;
      if (seenLabeled.has(sig)) return;
      seenLabeled.add(sig);
    }

    rows.push({
      key,
      label,
      value: display,
      isUrl: typeof value === "string" && value.includes("http"),
    });
  });

  return rows;
}

// "0.4" / "40" / "40%" -> "40%" (used for Bid Accuracy form scores, where the
// zap sends the job's score in paramB). Non-numeric values pass through.
/* `kpiMeta` is the linked KPI definition, when known — Quality Bid Accuracy
   keeps one decimal (97.3%); every other metric routed through here rounds
   to a whole percent like the rest of the app. */
export function formatScoreAsPercent(value, kpiMeta = null) {
  const n = Number(String(value).replace("%", "").trim());
  if (!Number.isFinite(n)) return String(value);
  const pct = n > 1 ? n : n * 100;
  if (isBidAccuracyKpi(kpiMeta)) return `${Math.round(pct * 10) / 10}%`;
  return `${Math.round(pct)}%`;
}

export function buildIntegratedParamFields(metadata, { formatScores = false, kpiMeta = null } = {}) {
  if (!metadata) return [];

  const fields = [];
  if (metadata.a != null && metadata.a !== "") {
    fields.push({ value: String(metadata.a) });
  }
  if (metadata.b != null && metadata.b !== "") {
    fields.push({
      value: formatScores ? formatScoreAsPercent(metadata.b, kpiMeta) : String(metadata.b),
    });
  }
  return fields;
}

export function isBidAccuracyCalcEntry(timeline) {
  return timeline?.metadata?.calc?.type === "bidAccuracy";
}

// Breakdown rows for a Bid Accuracy calc entry's "View Details" dialog, built
// from the computation snapshot the server stores on the monthly row:
// every completed job starts at 100%, submitted bid forms replace that job's
// score, and the metric is the average across jobs.
export function buildBidAccuracyDetailRows(timeline) {
  const calc = timeline?.metadata?.calc;
  if (!calc) return [];

  const jobs = Number(calc.completedJobs) || 0;
  const forms = Number(calc.formCount) || 0;
  const denominator = Math.max(jobs, forms);
  const noFormJobs = denominator - forms;
  const pct = (n) => `${Math.round(n * 1000) / 10}%`;

  const rows = [
    { key: "jobs", label: "Completed jobs (this month)", value: String(jobs) },
    { key: "forms", label: "Bid forms received", value: String(forms) },
  ];

  if (denominator) {
    rows.push({
      key: "noForm",
      label: "Jobs without a form (count as 100%)",
      value: String(noFormJobs),
    });
  }

  rows.push({
    key: "result",
    label: "Bid Accuracy",
    value: pct(Number(timeline.amount) || 0),
    strong: true,
  });

  return rows;
}

export function buildManualFormFields(timeline, kpi) {
  if (!timeline.metadata || !kpi?.requiredInputs?.length) return [];

  return kpi.requiredInputs
    .filter((input) => !["location", "quick-capture"].includes(input.type))
    .filter((input) => !KPI_IMAGE_PARAMS.includes(input.type))
    .map((input) => {
      const key = input.value;
      let value = timeline.metadata[key];
      if (value == null || value === "") {
        // Older entries can carry a different spelling of the same key than
        // the form currently uses (`plan_name ` vs `plan_name_`) — fall back
        // to the canonical name before giving up on the field.
        const altKey = Object.keys(timeline.metadata).find(
          (k) => canonicalMetaKey(k) === canonicalMetaKey(key ?? ""),
        );
        value = altKey != null ? timeline.metadata[altKey] : value;
      }
      if (value == null || value === "") return null;
      const label = (key ?? input.type)
        .replaceAll("_", " ")
        .replace(/\w+/g, capitalize)
        .trim();
      return { label, value: String(value) };
    })
    .filter(Boolean);
}

/** Absolute amount + "added to" / "deducted from" based on signed delta. */
export function getScoreChangeVerb(amount) {
  return Number(amount) < 0 ? "deducted from" : "added to";
}

export function formatScoreChangeDescription(amount, kind, kpiName = "", playerName = "") {
  const formattedAmount = formatNumber(Math.abs(Number(amount) || 0), kind);
  const verb = getScoreChangeVerb(amount);
  const name = kpiName || "Score";

  if (kind !== "$") {
    return `${formattedAmount} ${name} ${verb} ${playerName}`.trim();
  }

  return `A total of ${formattedAmount} ${name} was ${verb} ${playerName}`.trim();
}

export function buildIntegratedDescriptionParts(amount, kind, kpiName, playerName) {
  const formattedAmount = formatNumber(Math.abs(Number(amount) || 0), kind);
  const verb = getScoreChangeVerb(amount);
  const name = kpiName || "Score";

  if (kind !== "$") {
    return {
      prefix: "",
      amount: formattedAmount,
      kpiName: name,
      actionText: ` ${verb} `,
      playerName,
      suffix: "",
    };
  }

  return {
    prefix: "A total of ",
    amount: formattedAmount,
    kpiName: name,
    actionText: ` was ${verb} `,
    playerName,
    suffix: "",
  };
}

export function resolveJobSubtitlesFromJob(job) {
  if (!job) return { subtitle1: null, technicianName: null };

  const customerName =
    job.metadata?.customerName ||
    (job.customer?.first_name
      ? `${job.customer.first_name} ${job.customer.last_name}`.trim()
      : job.customer?.email);
  const description =
    job.metadata?.description || job.description || job.name || null;
  const subtitle1 = formatJobLeadName({ customerName, description });
  const technicianName =
    job.metadata?.technicianName ||
    (job.user?.firstName
      ? `${job.user.firstName} ${job.user.lastName}`.trim()
      : null);

  return { subtitle1, technicianName };
}

export function applyTimelineJobFields(ekpi) {
  const enriched = { ...ekpi };

  if (enriched.metadata?.refModel === "Job") {
    enriched.refModel = enriched.metadata.refModel;
    enriched.refId = enriched.metadata.refId;
  }

  const refId = getTimelineRefId(enriched);
  if (enriched.metadata?.extRefDate && refId) {
    enriched.createdAt = enriched.metadata.extRefDate;
  }

  return enriched;
}
